import React from 'react';

import {deprecatedTrackEvent} from 'analytics/Analytics';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import runQueryOutputToTSV from 'insights/analytics-queries/data/runQueryOutputToTSV';
import useResolvedRunQueryOutput from 'insights/analytics-visualizations/chip-items/useResolvedRunQueryOutput';
import {LegacyRunQueryState, RunQueryOutputLegacyData} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {sortedResolvedRunQueryOutput} from 'insights/warehouse/sortedRunQueryOutput';
import {RunQueryOutputTableSortState} from 'insights/warehouse/useRunQueryOutputTableSortState';
import {getMostRecentData} from 'ui-library/hooks/useAsyncState';
import Option from 'ui-library/menus/Option';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {assertNever} from 'util/Types';

/**
 * Content to display when the TSV data is loading
 */
function LoadingOptionContent(): JSX.Element {
  return <span className="u-faded">Preparing for copy...</span>;
}

/**
 * Component that loads TSV data and renders the Clipboard
 *
 * The TSV data to load is the chip item ids in runQueryOutput. If
 *   - SUCCESS -> [then it renders the] Clipboard for raw or full output depending on what mode the
 *                visualization is in
 *   - PENDING -> loading
 *   - ERROR, SKIPPED -> Clipboard for raw output, as a fallback
 */
function OptionContent(props: {
  runQueryOutput: RunQueryOutputLegacyData;
  showRawOutput: boolean;
  sortState: RunQueryOutputTableSortState;
  tsvTextRef: React.MutableRefObject<string | null>;
}): JSX.Element {
  const {queryResultType, resolvedRunQueryOutput} = useResolvedRunQueryOutput(props.runQueryOutput);
  switch (queryResultType) {
    case QueryResultType.PENDING:
      return <LoadingOptionContent />;
    case QueryResultType.SUCCESS:
      props.tsvTextRef.current = runQueryOutputToTSV(
        sortedResolvedRunQueryOutput(
          props.showRawOutput ? props.runQueryOutput : resolvedRunQueryOutput,
          props.sortState
        )
      );
      return <div>{props.showRawOutput ? 'Copy raw output to clipboard' : 'Copy to clipboard'}</div>;
    default:
      // Fallback (if query is not PENDING or SUCCESS) - raw output
      if (queryResultType !== QueryResultType.ERROR && queryResultType !== QueryResultType.SKIPPED) {
        assertNever(queryResultType);
      }
      props.tsvTextRef.current = runQueryOutputToTSV(props.runQueryOutput);
      return <div>Copy raw output to clipboard</div>;
  }
}

export interface CopyAsTSVOptionProps {
  runQueryState: LegacyRunQueryState;
  showRawOutput: boolean;
  sortState: RunQueryOutputTableSortState;
  // Since Menu expects all children to be Option components, and not components that wrap Option components,
  // getCopyAsTSVOption cannot return a Clipboard component that wraps the Option component.
  // However, without doing this, there is no way to make the entire option contained within a Clipboard
  // component (if the OptionContent is instead wrapped with <Clipboard>, clicking the edges of the option
  // will not trigger a copy). This necessitates the usage of copyToClipboard in the onSelect of the Option.
  //
  // copyToClipboard requires the text that will be copied, but we do not have access to the text this high
  // in the component tree and cannot use the required hooks to get the TSV string. We can't do so because
  // this is a function, not a hook or component. Therefore, we pass in tsvTextRef so that the TSV text can be
  // set by child components when it is available, and when selecting the option, the ref's value will be
  // used to access the value to copy.
  tsvTextRef: React.MutableRefObject<string | null>;
}

/**
 * Menu option to copy query results (runQueryOutput) as TSV to the clipboard
 */
export default function getCopyAsTSVOption({
  runQueryState,
  showRawOutput,
  sortState = null,
  tsvTextRef,
}: CopyAsTSVOptionProps): JSX.Element {
  const runQueryOutput = getMostRecentData(runQueryState);
  return (
    <Option
      icon="copy"
      // cannot include a check to the ref's value, otherwise the option will remain disabled even after
      // the ref value updates since a ref change would not trigger a re-render of this component.
      isDisabled={runQueryOutput == null}
      onSelect={() => {
        if (tsvTextRef.current) {
          deprecatedTrackEvent(INSIGHTS_ANALYTICS_ACTIONS.COPIED_QUERY_OUTPUT_AS_TSV);
          copyToClipboard(tsvTextRef.current, {
            showToastOnSuccess: true,
            toastText: 'Copied to clipboard.',
          });
        }
      }}
      dataTestAttrs={{
        component: 'BaseBlockOverflowDropdown',
        key: 'copyAsTSV',
      }}
    >
      {runQueryOutput != null ? (
        <OptionContent
          runQueryOutput={runQueryOutput}
          showRawOutput={showRawOutput}
          sortState={sortState}
          tsvTextRef={tsvTextRef}
        />
      ) : (
        <LoadingOptionContent />
      )}
    </Option>
  );
}
